// src/pages/CRM/Companies/CompanyForm.tsx

import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCompanyDetail } from "../../../hooks/useCompanies";
import { useCreateCompany, useUpdateCompany } from "../../../query/companies";
import { Button } from "../../../components/Button";
import { FormInput } from "../../../components/FormField";
import { FormSelect } from "../../../components/FormField";
import { ArrowLeft, Save } from "lucide-react";
import { crmRoutePaths } from "../../../routes/crmRoutes";
import { Industry, CompanySize, LeadSource, CompanyStatus } from "../../../api/crmApi";
import { companySchema, CompanyFormValues } from "../../../validation";
import { toast } from "sonner";
import { COMPANY_STATUS_OPTIONS, LEAD_SOURCE_OPTIONS } from "../../../utils/constants";
import { handleApiFormError } from "../../../utils/apiError";

const defaultValues: CompanyFormValues = {
    name: "",
    website: "",
    industry: Industry.TECHNOLOGY,
    companySize: undefined,
    status: CompanyStatus.PROSPECT,
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    leadSource: LeadSource.WEBSITE,
    numberOfEmployees: undefined,
    annualRevenue: undefined,
    linkedinUrl: "",
    timezone: "",
    description: "",
};

const CompanyForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const { company, loading } = useCompanyDetail(id);
    const createMutation = useCreateCompany();
    const updateMutation = useUpdateCompany();

    const {
        register,
        handleSubmit,
        control,
        reset,
        setError,
        formState: { errors },
    } = useForm<CompanyFormValues>({
        resolver: zodResolver(companySchema),
        defaultValues,
    });

    useEffect(() => {
        if (isEditMode && company) {
            reset({
                name: company.name,
                website: company.website,
                industry: company.industry,
                companySize: company.companySize,
                status: company.status || CompanyStatus.PROSPECT,
                email: company.email,
                phone: company.phone,
                address: company.address,
                city: company.city,
                state: company.state,
                postalCode: company.postalCode,
                country: company.country,
                leadSource: company.leadSource,
                numberOfEmployees: company.numberOfEmployees,
                annualRevenue: company.annualRevenue,
                linkedinUrl: company.linkedinUrl || "",
                timezone: company.timezone || "",
                description: company.description || "",
            });
        }
    }, [isEditMode, company, reset]);

    const onSubmit = async (data: CompanyFormValues) => {
        try {
            if (isEditMode && id) {
                await updateMutation.mutateAsync({ id, payload: data });
                toast.success("Company updated successfully");
                navigate(`${crmRoutePaths.companies}/${id}`);
            } else {
                const newCompany = await createMutation.mutateAsync(data);
                toast.success("Company created successfully");
                navigate(`${crmRoutePaths.companies}/${newCompany.id}`);
            }
        } catch (error) {
            handleApiFormError(error, setError);
        }
    };

    const handleCancel = () => {
        if (isEditMode && id) {
            navigate(`${crmRoutePaths.companies}/${id}`);
        } else {
            navigate(crmRoutePaths.companies);
        }
    };

    if (isEditMode && loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="h-full bg-page-bg overflow-auto">
            <div className="max-w-4xl mx-auto py-8 px-6">
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancel}
                        className="mb-4">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <h1 className="text-3xl font-bold text-gray-900">
                        {isEditMode ? "Edit Company" : "New Company"}
                    </h1>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-6">

                        {/* Company Information */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">
                                Company Information
                            </h2>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="col-span-2">
                                    <FormInput
                                        label="Company Name"
                                        required
                                        id="name"
                                        error={errors.name?.message}
                                        {...register("name")}
                                    />
                                </div>

                                <Controller
                                    name="industry"
                                    control={control}
                                    render={({ field }) => (
                                        <FormSelect
                                            label="Industry"
                                            required
                                            id="industry"
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            error={errors.industry?.message}
                                            data={[
                                                { value: Industry.TECHNOLOGY, label: "Technology" },
                                                { value: Industry.FINANCE, label: "Finance" },
                                                { value: Industry.HEALTHCARE, label: "Healthcare" },
                                                { value: Industry.RETAIL, label: "Retail" },
                                                { value: Industry.MANUFACTURING, label: "Manufacturing" },
                                                { value: Industry.EDUCATION, label: "Education" },
                                                { value: Industry.REAL_ESTATE, label: "Real Estate" },
                                            ]}
                                        />
                                    )}
                                />

                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <FormSelect
                                            label="Status"
                                            id="status"
                                            value={field.value || ""}
                                            onValueChange={field.onChange}
                                            placeholder="Select status"
                                            data={COMPANY_STATUS_OPTIONS}
                                            error={errors.status?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="leadSource"
                                    control={control}
                                    render={({ field }) => (
                                        <FormSelect
                                            label="Lead Source"
                                            required
                                            id="leadSource"
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            error={errors.leadSource?.message}
                                            data={LEAD_SOURCE_OPTIONS}
                                        />
                                    )}
                                />

                                <FormInput
                                    label="Website"
                                    required
                                    id="website"
                                    type="url"
                                    placeholder="https://example.com"
                                    error={errors.website?.message}
                                    {...register("website")}
                                />

                                <FormInput
                                    label="Email"
                                    required
                                    id="email"
                                    type="email"
                                    error={errors.email?.message}
                                    {...register("email")}
                                />

                                <FormInput
                                    label="Phone"
                                    required
                                    id="phone"
                                    type="tel"
                                    error={errors.phone?.message}
                                    {...register("phone")}
                                />

                                <Controller
                                    name="companySize"
                                    control={control}
                                    render={({ field }) => (
                                        <FormSelect
                                            label="Company Size"
                                            id="companySize"
                                            value={field.value ?? ""}
                                            onValueChange={field.onChange}
                                            placeholder="Select size"
                                            error={errors.companySize?.message}
                                            data={[
                                                { value: CompanySize.SIZE_1_10, label: "1-10" },
                                                { value: CompanySize.SIZE_11_50, label: "11-50" },
                                                { value: CompanySize.SIZE_51_200, label: "51-200" },
                                                { value: CompanySize.SIZE_201_500, label: "201-500" },
                                                { value: CompanySize.SIZE_501_1000, label: "501-1000" },
                                                { value: CompanySize.SIZE_1000_PLUS, label: "1000+" },
                                            ]}
                                        />
                                    )}
                                />

                                <FormInput
                                    label="Number of Employees"
                                    id="numberOfEmployees"
                                    type="number"
                                    min={1}
                                    placeholder="e.g. 250"
                                    error={errors.numberOfEmployees?.message}
                                    {...register("numberOfEmployees", {
                                        setValueAs: (v) => (v === "" ? undefined : parseInt(v, 10)),
                                    })}
                                />

                                <FormInput
                                    label="Annual Revenue (cents)"
                                    id="annualRevenue"
                                    type="number"
                                    min={0}
                                    placeholder="e.g. 5000000"
                                    error={errors.annualRevenue?.message}
                                    {...register("annualRevenue", {
                                        setValueAs: (v) => (v === "" ? undefined : parseInt(v, 10)),
                                    })}
                                />

                                <FormInput
                                    label="LinkedIn URL"
                                    id="linkedinUrl"
                                    type="url"
                                    placeholder="https://linkedin.com/company/..."
                                    error={errors.linkedinUrl?.message}
                                    {...register("linkedinUrl")}
                                />

                                <FormInput
                                    label="Timezone"
                                    id="timezone"
                                    placeholder="e.g. Australia/Sydney"
                                    error={errors.timezone?.message}
                                    {...register("timezone")}
                                />

                                <div className="col-span-2">
                                    <FormInput
                                        label="Description"
                                        id="description"
                                        error={errors.description?.message}
                                        {...register("description")}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Address */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">Address</h2>
                            <div className="space-y-4">
                                <FormInput
                                    label="Street Address"
                                    required
                                    id="address"
                                    error={errors.address?.message}
                                    {...register("address")}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <FormInput
                                        label="City"
                                        required
                                        id="city"
                                        error={errors.city?.message}
                                        {...register("city")}
                                    />

                                    <FormInput
                                        label="State / Province"
                                        required
                                        id="state"
                                        error={errors.state?.message}
                                        {...register("state")}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <FormInput
                                        label="Postal Code"
                                        required
                                        id="postalCode"
                                        error={errors.postalCode?.message}
                                        {...register("postalCode")}
                                    />

                                    <FormInput
                                        label="Country"
                                        required
                                        id="country"
                                        error={errors.country?.message}
                                        {...register("country")}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex items-center justify-end space-x-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={createMutation.isPending || updateMutation.isPending}>
                                {createMutation.isPending || updateMutation.isPending ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        {isEditMode ? "Update Company" : "Create Company"}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CompanyForm;
